import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { FormRow } from '../../components/ui/FormRow';
import { Tabs } from '../../components/ui/Tabs';
import { DataTable, type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { supabase } from '../../lib/supabase';
import { avisoSinCoste } from '../../lib/avisoSinCoste';
import { toast, confirmar } from '../../lib/toast';
import { EntregarPedidoModal } from '../../components/pedido-cliente/EntregarPedidoModal';
import { FusionarPedidosModal } from '../../components/pedido-cliente/FusionarPedidosModal';
import { ESTADOS_CONFIRMADOS } from '../../../shared/comisiones';

// Estados en los que se puede registrar una entrega desde el listado.
const ESTADOS_ENTREGABLES = new Set<string>(ESTADOS_CONFIRMADOS.filter((e) => e !== 'FINALIZADO'));

type PedidoClienteRow = {
  id: string;
  fecha_pedido: string;
  fecha_pedido_confirmado: string | null;
  numero_approx: string | null;
  numero_cliente: string | null;
  pi_proveedor: string | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  notas: string | null;
  lineas_count: number;
  unidades_total: number;
  subtotal_eur: number | string | null;
  unidades_entregadas_total: number;
  archivos_count: number;
  ultima_entrega_fecha: string | null;
  etd_pedido: string | null;
  eta_pedido: string | null;
  fecha_entrega_solicitada_pedido: string | null;
  created_at: string;
};

type LineaRow = {
  linea_id: string;
  pedido_cliente_id: string;
  fecha_pedido: string;
  numero_cliente: string | null;
  numero_approx: string | null;
  pi_proveedor: string | null;
  sku_cliente: string | null;
  pn_ref: string | null;
  descripcion: string | null;
  ean: string | null;
  unidades: number;
  unidades_entregadas: number;
  unidades_pendientes: number;
  precio_unitario: number | string | null;
  etd: string | null;
  eta: string | null;
  fecha_entrega_solicitada: string | null;
  fecha_entrega_real: string | null;
  cotizacion_id: string | null;
  producto_id: string | null;
  aceptada: boolean;
  producto_sku: string | null;
  producto_nombre: string | null;
  producto_ean: string | null;
  subcategoria_nombre: string | null;
  categoria_nombre: string | null;
  gama_nombre: string | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  pedido_proveedor_id: string | null;
  pedido_proveedor_estado: string | null;
  ultima_entrega_fecha: string | null;
};

type ImportPreviewLinea = {
  sku: string;
  referencia: string | null;
  producto: string | null;
  unidades: number;
  precio_unitario_eur: number | string | null;
  entrega_estimada: string | null;
};

type ImportPreviewMatch = {
  sku: string;
  producto_id: string | null;
  producto_nombre: string | null;
};

type ImportPreview = {
  parsed: {
    numero_pedido_game: string | null;
    fecha_pedido: string | null;
    /** TICNOVA: «Su referencia» del PDF (la PI de APPROX). */
    su_referencia?: string | null;
    lineas: ImportPreviewLinea[];
    /** Descuadres contra el bloque TOTALES del propio PDF. Si trae algo, faltan líneas. */
    avisos?: string[];
  };
  matches: ImportPreviewMatch[];
  /** Si el pedido ya existe: qué se hará y en qué difiere del documento. */
  verificacion?: Verificacion;
};

/** Respuesta de verificarPedido() en el servidor (lib/pedido-existente.ts). */
type Verificacion = {
  accion: 'crear' | 'adjuntar' | 'nada';
  existente: { id: string; numero_cliente: string; estado: string } | null;
  comparacion: {
    coincide: boolean;
    diferencias: string[];
    documento: { lineas: number; unidades: number; importe: number };
    registrado: { lineas: number; unidades: number; importe: number };
  } | null;
  documento_existente: { nombre: string; bytes: number } | null;
  documento: 'identico' | 'mismo_contenido' | 'distinto' | 'no_comprobado' | null;
};

/** Respuesta de /import-ticnova-pdf, llevada a la forma de la de GAME. */
type TicnovaPdfResponse = {
  parsed: {
    numero_pedido: string | null; fecha_pedido: string | null; su_referencia: string | null; avisos: string[];
    lineas: { sku: string; referencia: string; descripcion: string; unidades: number; precio_unitario_eur: number; entrega: string | null }[];
  };
  matches: ImportPreviewMatch[];
  verificacion: Verificacion;
};

function desdeTicnova(r: TicnovaPdfResponse): ImportPreview {
  return {
    parsed: {
      numero_pedido_game: r.parsed.numero_pedido,
      fecha_pedido: r.parsed.fecha_pedido,
      su_referencia: r.parsed.su_referencia,
      avisos: r.parsed.avisos,
      lineas: r.parsed.lineas.map((l) => ({ sku: l.sku, referencia: l.referencia, producto: l.descripcion, unidades: l.unidades, precio_unitario_eur: l.precio_unitario_eur, entrega_estimada: l.entrega })),
    },
    matches: r.matches,
    verificacion: r.verificacion,
  };
}

type ExcelGrupoLinea = {
  rowNum: number;
  sku: string | null;
  ean: string | null;
  ref: string | null;
  producto: string | null;
  coste: number | null;
  unidades: number;
  fecha_estimada: string | null;
  producto_id: string | null;
  producto_match_nombre: string | null;
};

type ExcelGrupo = {
  numero: string;
  fecha_pedido: string | null;
  lineas: ExcelGrupoLinea[];
  existente?: { id: string; estado: string; lineas_actuales: number } | null;
};

type ExcelDecision = 'crear' | 'sobrescribir' | 'ignorar';

type ExcelImportPreview = {
  parsed: { grupos: ExcelGrupo[]; total_filas: number };
  headers_detectados: Record<string, number>;
};

const ESTADOS = [
  { value: '__abiertos__', label: 'Pedidos abiertos (todos menos cerrados)' },
  { value: '', label: 'Todos los estados' },
  { value: 'PDTE_RESPUESTA', label: 'Pdte. respuesta' },
  { value: 'ACEPTADO', label: 'Aceptado' },
  { value: 'EN_PRODUCCION', label: 'En producción' },
  { value: 'EN_CAMINO', label: 'En camino' },
  { value: 'ENTREGA_PARCIAL', label: 'Entrega parcial' },
  { value: 'ENTREGADO', label: 'Entregado' },
  { value: 'FINALIZADO', label: 'Finalizado' },
  { value: 'INCIDENCIA', label: 'Incidencia' },
  { value: 'RECHAZADO', label: 'Rechazado' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

// Cuadrado de importación con arrastrar-y-soltar (o clic para elegir archivo).
function ZonaImport({ icono, titulo, hint, busy, busyLabel, onClick, onFile, compacta = false }: {
  compacta?: boolean;
  icono: string;
  titulo: string;
  hint: string;
  busy: boolean;
  busyLabel: string;
  onClick: () => void;
  onFile: (f: File) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onClick={() => { if (!busy) onClick(); }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (busy) return;
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={`flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors ${
        compacta ? 'gap-2 px-3 py-1.5' : 'flex-col px-4 py-5'
      } ${
        dragOver ? 'border-sky-400 bg-sky-50' : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50'
      }`}
      title={hint}
    >
      {compacta ? (
        <>
          <span className="text-lg">{icono}</span>
          <span className="text-left leading-tight">
            <span className="block text-sm font-semibold text-slate-700">{busy ? busyLabel : titulo}</span>
            <span className="block text-[11px] text-slate-500">{busy ? 'Espera un momento…' : 'Arrastra el PDF o haz clic'}</span>
          </span>
        </>
      ) : <>
      <span className="text-2xl">{icono}</span>
      <p className="mt-1 text-sm font-semibold text-slate-700">{busy ? busyLabel : titulo}</p>
      <p className="mt-0.5 text-xs text-slate-500">{busy ? 'Espera un momento…' : 'Arrastra el archivo aquí o haz clic para elegirlo'}</p>
      </>}
    </div>
  );
}

export function PedidosClienteList({
  clienteCodigo, clienteId, title, subtitle,
}: { clienteCodigo: string; clienteId: string | null; title: string; subtitle: string }) {
  const navigate = useNavigate();
  // Base de ruta según el cliente, para que TICNOVA no caiga en /game.
  const base = clienteCodigo === 'TICNOVA' ? '/ticnova' : '/game';
  const qc = useQueryClient();
  const [estado, setEstado] = useState('__abiertos__');
  // Se abre en «Por pedido» (CEO, 22/10/2026).
  const [tab, setTab] = useState<'productos' | 'pedidos'>('pedidos');
  // Pedidos desplegados con +/− en la pestaña «Por pedido».
  const [desplegados, setDesplegados] = useState<Set<string>>(new Set());
  const toggleDesplegado = useCallback((id: string) => {
    setDesplegados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const [search, setSearch] = useState('');
  const [entregarPedido, setEntregarPedido] = useState<PedidoClienteRow | null>(null);
  const [fusionarOpen, setFusionarOpen] = useState(false);
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [draft, setDraft] = useState({
    fecha_pedido: new Date().toISOString().slice(0, 10),
    numero_approx: '',
    numero_cliente: '',
  });

  // Listado de cabeceras (pestaña "Por pedido")
  const cabeceras = useQuery({
    queryKey: ['pedidos-cliente', clienteCodigo, estado],
    queryFn: () => api.get<{ data: PedidoClienteRow[] }>(`/pedidos-cliente?cliente_codigo=${clienteCodigo}${estado ? `&estado=${estado}` : ''}`),
    enabled: tab === 'pedidos',
  });

  // Listado por línea (pestaña principal "Por producto")
  const lineas = useQuery({
    queryKey: ['pedidos-cliente-lineas', clienteCodigo, estado, search],
    queryFn: () => {
      const qs = new URLSearchParams({ cliente_codigo: clienteCodigo });
      if (estado) qs.set('estado', estado);
      if (search.trim()) qs.set('q', search.trim());
      return api.get<{ data: LineaRow[] }>(`/pedidos-cliente/lineas/full?${qs.toString()}`);
    },
    enabled: tab === 'productos',
  });

  // Selección múltiple + bulk de estado. Mantenemos dos sets independientes
  // (uno por pestaña) pero el bulkMut es compartido — el applyBulk decide
  // qué ids de pedido enviar según la pestaña activa.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedLineaIds, setSelectedLineaIds] = useState<Set<string>>(new Set());
  const [bulkEstado, setBulkEstado] = useState('');
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Pedidos únicos derivados de las líneas seleccionadas en la pestaña productos.
  const pedidoIdsDesdeLineas = useMemo(() => {
    const set = new Set<string>();
    const data = lineas.data?.data ?? [];
    for (const l of data) {
      if (selectedLineaIds.has(l.linea_id)) set.add(l.pedido_cliente_id);
    }
    return set;
  }, [lineas.data, selectedLineaIds]);

  const [cancelacionOpen, setCancelacionOpen] = useState(false);
  const [cancelacionMotivo, setCancelacionMotivo] = useState('');
  const [cancelacionIds, setCancelacionIds] = useState<string[]>([]);

  // ENTREGADO en masa: ventana con calendario para la fecha de entrega.
  const [entregadoIds, setEntregadoIds] = useState<string[] | null>(null);
  const [entregadoFecha, setEntregadoFecha] = useState('');

  const bulkMut = useMutation({
    mutationFn: (args: { pedidoIds: string[]; motivo?: string; fechaEntrega?: string }) => api.patch<{ updated: number; pedidos_proveedor: { creados: number; ya_existentes: number; sin_producto: number; sin_coste: number; sin_coste_lineas: string[] } | null }>('/pedidos-cliente/bulk', {
      ids: args.pedidoIds,
      updates: { estado: bulkEstado },
      motivo_cancelacion: args.motivo,
      fecha_entrega: args.fechaEntrega,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setSelectedIds(new Set());
      setSelectedLineaIds(new Set());
      setBulkEstado('');
      setBulkError(null);
      if (r.pedidos_proveedor) {
        const aviso = avisoSinCoste(r.pedidos_proveedor);
        (aviso ? toast.warning : toast.success)('Pedidos a proveedor generados', `${r.pedidos_proveedor.creados} creado(s) (${r.pedidos_proveedor.ya_existentes} ya existían, ${r.pedidos_proveedor.sin_producto} sin producto).${aviso}`);
      }
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const m = msg.match(/→\s*\d+:\s*(.+)$/);
      setBulkError(m ? m[1] : msg);
    },
  });
  const applyBulk = async () => {
    if (!bulkEstado) return;
    const ids = tab === 'pedidos'
      ? Array.from(selectedIds)
      : Array.from(pedidoIdsDesdeLineas);
    if (ids.length === 0) return;
    // CANCELADO siempre abre un modal para pedir motivo (en cualquier tab).
    if (bulkEstado === 'CANCELADO') {
      setCancelacionIds(ids);
      setCancelacionMotivo('');
      setCancelacionOpen(true);
      return;
    }
    // ENTREGADO siempre pide la fecha de entrega (hoy preseleccionado).
    if (bulkEstado === 'ENTREGADO') {
      setEntregadoFecha(hoyLocal());
      setEntregadoIds(ids);
      return;
    }
    const detalle = tab === 'pedidos'
      ? `${ids.length} pedido(s)`
      : `${selectedLineaIds.size} línea(s) — afecta al pedido completo de cada línea (${ids.length} pedido(s) en total).\nEl cambio de estado se aplica al pedido entero, no solo a las líneas seleccionadas.`;
    if (!(await confirmar({
      title: `¿Cambiar estado a "${bulkEstado}"?`,
      message: `Se aplica a ${detalle}${bulkEstado === 'ACEPTADO' ? '\n\nPasar a ACEPTADO genera los pedidos a proveedor de todas las líneas con producto.' : ''}`,
      confirmLabel: 'Cambiar estado',
    }))) return;
    bulkMut.mutate({ pedidoIds: ids });
  };

  const confirmarCancelacionBulk = async () => {
    if (!cancelacionMotivo.trim()) {
      if (!(await confirmar({ title: '¿Cancelar sin indicar motivo?', message: 'No has indicado motivo de cancelación.', confirmLabel: 'Cancelar igualmente', danger: true }))) return;
    }
    bulkMut.mutate({ pedidoIds: cancelacionIds, motivo: cancelacionMotivo.trim() || undefined }, {
      onSuccess: () => {
        setCancelacionOpen(false);
        setCancelacionIds([]);
        setCancelacionMotivo('');
      },
    });
  };

  const confirmarEntregadoBulk = () => {
    if (!entregadoIds || !entregadoFecha) return;
    bulkMut.mutate({ pedidoIds: entregadoIds, fechaEntrega: entregadoFecha }, {
      onSuccess: () => setEntregadoIds(null),
    });
  };

  const crear = useMutation({
    mutationFn: () => api.post<{ id: string }>('/pedidos-cliente', { cliente_id: clienteId, ...draft }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      setNuevoOpen(false);
      navigate(`${base}/pedidos/${r.id}`);
    },
  });

  // Importación PDF GAME — flujo en 2 pasos:
  //   1) usuario elige fichero → POST commit=false → modal con preview
  //   2) usuario confirma     → POST commit=true → crea pedido + adjunta PDF
  // El fichero se mantiene en estado entre los dos pasos para no obligar
  // a re-seleccionarlo (y para que el adjunto sea exactamente el PDF que vio).
  const esTicnova = clienteCodigo === 'TICNOVA';
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputExcelRef = useRef<HTMLInputElement | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importWarning, setImportWarning] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreview | null>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelPreview, setExcelPreview] = useState<ExcelImportPreview | null>(null);
  const [excelDecisiones, setExcelDecisiones] = useState<Record<string, ExcelDecision>>({});
  const [excelHelpOpen, setExcelHelpOpen] = useState(false);

  const postImport = async (file: File, commit: boolean) => {
    const { data: sess } = await supabase.auth.getSession();
    const token = sess.session?.access_token;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/pedidos-cliente/import-${esTicnova ? 'ticnova' : 'game'}-pdf?commit=${commit ? 'true' : 'false'}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((body as { error?: string; message?: string }).message
        ?? (body as { error?: string }).error
        ?? `Import falló (${res.status})`);
    }
    return body;
  };

  const previewPdf = useMutation({
    mutationFn: async (file: File) => {
      const r = await postImport(file, false);
      return esTicnova ? desdeTicnova(r as TicnovaPdfResponse) : (r as ImportPreview);
    },
    onSuccess: (r, file) => {
      setImportError(null);
      setPreviewFile(file);
      setPreviewData(r);
    },
    onError: (err) => {
      setImportError((err as Error).message);
      setPreviewFile(null);
      setPreviewData(null);
    },
  });

  const confirmarImport = useMutation({
    mutationFn: () => {
      if (!previewFile) throw new Error('no_file');
      return postImport(previewFile, true) as Promise<{ pedido_cliente_id: string; accion?: Verificacion['accion']; verificacion?: Verificacion; archivo_warning?: string | null }>;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setImportError(null);
      const numero = r.verificacion?.existente?.numero_cliente ?? '';
      if (r.accion === 'adjuntar' && !r.archivo_warning) {
        const dif = r.verificacion?.comparacion?.coincide === false ? ' Ojo: el pedido registrado no coincide del todo con el documento; revisa las diferencias.' : '';
        (dif ? toast.warning : toast.success)('PDF adjuntado al pedido existente', `El pedido ${numero} ya estaba registrado sin documento: se le ha adjuntado el PDF.${dif}`);
      } else if (r.accion === 'nada') {
        toast.info('No se ha cambiado nada', `El pedido ${numero} ya estaba registrado con su documento.`);
      }
      if (r.archivo_warning) setImportWarning(r.archivo_warning);
      setPreviewFile(null);
      setPreviewData(null);
      navigate(`${base}/pedidos/${r.pedido_cliente_id}`);
    },
    onError: (err) => setImportError((err as Error).message),
  });

  const cancelarImport = () => {
    setPreviewFile(null);
    setPreviewData(null);
    setImportError(null);
  };

  // ── Importador Excel (flujo paralelo al PDF) ─────────────────────────
  const postImportExcel = async (file: File, commit: boolean, decisiones?: Record<string, ExcelDecision>) => {
    const { data: sess } = await supabase.auth.getSession();
    const token = sess.session?.access_token;
    const fd = new FormData();
    fd.append('file', file);
    if (decisiones) fd.append('decisiones', JSON.stringify(decisiones));
    const res = await fetch(`/api/pedidos-cliente/import-game-excel?commit=${commit ? 'true' : 'false'}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((body as { error?: string; message?: string }).message
        ?? (body as { error?: string }).error
        ?? `Import Excel falló (${res.status})`);
    }
    return body;
  };

  const previewExcel = useMutation({
    mutationFn: (file: File) => postImportExcel(file, false) as Promise<ExcelImportPreview>,
    onSuccess: (r, file) => {
      setImportError(null);
      setExcelFile(file);
      setExcelPreview(r);
      // Inicializa decisiones: nuevos→crear, existentes→ignorar (safe default).
      const init: Record<string, ExcelDecision> = {};
      for (const g of r.parsed.grupos) {
        init[g.numero] = g.existente ? 'ignorar' : 'crear';
      }
      setExcelDecisiones(init);
    },
    onError: (err) => {
      setImportError((err as Error).message);
      setExcelFile(null);
      setExcelPreview(null);
      setExcelDecisiones({});
    },
  });

  const confirmarExcel = useMutation({
    mutationFn: () => {
      if (!excelFile) throw new Error('no_file');
      return postImportExcel(excelFile, true, excelDecisiones) as Promise<{
        pedidos_creados: { id: string; numero: string; lineas: number }[];
        pedidos_sobrescritos: { id: string; numero: string; lineas: number; lineas_borradas: number }[];
        pedidos_ignorados: { id: string | null; numero: string }[];
        productos_creados: { id: string; sku: string }[];
        total_filas: number;
      }>;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setImportError(null);
      setExcelFile(null);
      setExcelPreview(null);
      setExcelDecisiones({});
      const totalAccion = r.pedidos_creados.length + r.pedidos_sobrescritos.length;
      if (totalAccion === 1) {
        const dest = r.pedidos_creados[0]?.id ?? r.pedidos_sobrescritos[0]?.id;
        if (dest) navigate(`${base}/pedidos/${dest}`);
      } else {
        const msg = [
          `Creados: ${r.pedidos_creados.length}`,
          `Sobrescritos: ${r.pedidos_sobrescritos.length}`,
          `Ignorados: ${r.pedidos_ignorados.length}`,
          `Productos nuevos: ${r.productos_creados.length}`,
        ].join('\n');
        toast.success('Importación Excel completada', msg);
      }
    },
    onError: (err) => setImportError((err as Error).message),
  });

  const cancelarExcel = () => {
    setExcelFile(null);
    setExcelPreview(null);
    setExcelDecisiones({});
    setImportError(null);
  };

  // ── Bulk delete (cabeceras y líneas) ─────────────────────────────────
  const bulkDeletePedidos = useMutation({
    mutationFn: (ids: string[]) => api.post<{ deleted: number }>('/pedidos-cliente/bulk-delete', { ids }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setSelectedIds(new Set());
      toast.success(`Eliminados ${r.deleted} pedido(s)`);
    },
    onError: (err) => setBulkError((err as Error).message),
  });
  const bulkDeleteLineas = useMutation({
    mutationFn: (ids: string[]) => api.post<{ deleted: number }>('/pedidos-cliente/lineas/bulk-delete', { ids }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setSelectedLineaIds(new Set());
      toast.success(`Eliminadas ${r.deleted} línea(s)`);
    },
    onError: (err) => setBulkError((err as Error).message),
  });

  // ── Bulk-update de fechas en líneas ──────────────────────────────
  const [fechasBulkOpen, setFechasBulkOpen] = useState(false);
  const [fechasDraft, setFechasDraft] = useState({ fecha_entrega_solicitada: '', etd: '', eta: '' });
  const fechasBulkMut = useMutation({
    mutationFn: () => {
      const updates: Record<string, string | null> = {};
      if (fechasDraft.fecha_entrega_solicitada) updates.fecha_entrega_solicitada = fechasDraft.fecha_entrega_solicitada;
      if (fechasDraft.etd) updates.etd = fechasDraft.etd;
      if (fechasDraft.eta) updates.eta = fechasDraft.eta;
      if (tab === 'pedidos') {
        // Aplicar a TODAS las líneas de los pedidos seleccionados.
        return api.post<{ updated: number; pedidos_afectados: number }>(
          '/pedidos-cliente/bulk-update-fechas',
          { ids: Array.from(selectedIds), updates },
        );
      }
      // Tab líneas: solo las líneas seleccionadas.
      return api.post<{ updated: number; pedidos_afectados?: number }>(
        '/pedidos-cliente/lineas/bulk-update',
        { ids: Array.from(selectedLineaIds), updates },
      );
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      setFechasBulkOpen(false);
      setFechasDraft({ fecha_entrega_solicitada: '', etd: '', eta: '' });
      const msg = tab === 'pedidos'
        ? `Actualizadas ${r.updated} línea(s) en ${r.pedidos_afectados ?? '?'} pedido(s)`
        : `Actualizadas ${r.updated} línea(s)`;
      toast.success(msg);
    },
    onError: (err) => toast.error('No se pudieron actualizar las fechas', (err as Error).message),
  });

  // ── Entrega parcial / total por línea ─────────────────────────────
  const [entregaLinea, setEntregaLinea] = useState<LineaRow | null>(null);
  const [entregaUds, setEntregaUds] = useState<string>('');
  const [entregaFecha, setEntregaFecha] = useState<string>('');
  const [entregaNotas, setEntregaNotas] = useState<string>('');
  const [entregaError, setEntregaError] = useState<string | null>(null);
  const abrirEntrega = useCallback((linea: LineaRow) => {
    setEntregaLinea(linea);
    setEntregaUds(String(Math.max(0, linea.unidades_pendientes || linea.unidades)));
    setEntregaFecha(new Date().toISOString().slice(0, 10));
    setEntregaNotas('');
    setEntregaError(null);
  }, []);
  const cerrarEntrega = () => {
    setEntregaLinea(null); setEntregaUds(''); setEntregaFecha(''); setEntregaNotas(''); setEntregaError(null);
  };

  const entregar = useMutation({
    mutationFn: ({ confirmar }: { confirmar?: boolean }) => {
      if (!entregaLinea) throw new Error('no_linea');
      const uds = Number(entregaUds);
      if (!Number.isFinite(uds) || uds <= 0) throw new Error('Indica un número de unidades > 0');
      return api.post<{
        unidades_previstas: number;
        unidades_entregadas: number;
        unidades_pendientes: number;
        sobre_entrega: boolean;
        estado_pedido: { estado_anterior: string; estado_nuevo: string } | null;
      }>(
        `/pedidos-cliente/${entregaLinea.pedido_cliente_id}/lineas/${entregaLinea.linea_id}/entregar`,
        {
          unidades: uds,
          fecha_entrega: entregaFecha || undefined,
          notas: entregaNotas || undefined,
          confirmar_sobreentrega: confirmar === true,
        },
      );
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      cerrarEntrega();
      const estado = r.estado_pedido?.estado_nuevo;
      if (estado === 'FINALIZADO') {
        toast.success('Línea entregada', 'Pedido pasa a FINALIZADO (todas las líneas entregadas).');
      } else if (estado === 'ENTREGA_PARCIAL') {
        toast.success('Línea entregada', `Quedan ${r.unidades_pendientes} ud. pdtes. en esta línea. Pedido en ENTREGA_PARCIAL.`);
      } else {
        // mismo estado
      }
    },
    onError: async (err) => {
      const msg = (err as Error).message;
      // Detectamos el caso de sobre-entrega (409) que el backend devuelve
      // como `sobre_entrega` + message — preguntamos al CEO si confirma.
      if (/sobre_entrega|409/i.test(msg)) {
        const m = msg.match(/→\s*\d+:\s*(.+)$/);
        const txt = m ? m[1] : msg;
        if (await confirmar({ title: '¿Confirmar la sobre-entrega y cerrar el pedido?', message: txt, confirmLabel: 'Confirmar sobre-entrega', danger: true })) {
          entregar.mutate({ confirmar: true });
          return;
        }
      }
      const m = msg.match(/→\s*\d+:\s*(.+)$/);
      setEntregaError(m ? m[1] : msg);
    },
  });

  // Aceptar UNA línea (por producto): el backend da de alta su producto y genera
  // su pedido a proveedor, sin tocar las demás líneas. Cuando todas quedan
  // aceptadas, el pedido pasa a ACEPTADO.
  const marcarAceptado = useMutation({
    mutationFn: ({ pedidoClienteId, lineaId }: { pedidoClienteId: string; lineaId: string }) =>
      api.post<{
        pedidos_proveedor: { creados: number; sin_coste: number; sin_coste_lineas: string[] } | null;
        productos_creados: { creados: number } | null;
        estado_pedido: { estado_nuevo: string } | null;
      }>(`/pedidos-cliente/${pedidoClienteId}/lineas/${lineaId}/aceptar`, {}),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      const extra = r.estado_pedido?.estado_nuevo === 'ACEPTADO' ? ' Todas las líneas aceptadas → pedido ACEPTADO.' : '';
      const aviso = avisoSinCoste(r.pedidos_proveedor);
      (aviso ? toast.warning : toast.success)('Producto aceptado', `Producto(s) creado(s): ${r.productos_creados?.creados ?? 0}. Pedido(s) a proveedor: ${r.pedidos_proveedor?.creados ?? 0}.${extra}${aviso}`);
    },
    onError: (err) => toast.error('No se pudo aceptar el producto', (err as Error).message),
  });

  const deleteBulkHandler = async () => {
    if (tab === 'pedidos') {
      if (selectedIds.size === 0) return;
      if (!(await confirmar({ title: `¿Eliminar ${selectedIds.size} pedido(s)?`, message: 'Se ocultan pero quedan en el audit log.', confirmLabel: 'Eliminar', danger: true }))) return;
      bulkDeletePedidos.mutate(Array.from(selectedIds));
    } else {
      if (selectedLineaIds.size === 0) return;
      if (!(await confirmar({ title: `¿Eliminar ${selectedLineaIds.size} línea(s)?`, message: 'Esta acción es definitiva (las líneas no tienen soft delete).', confirmLabel: 'Eliminar', danger: true }))) return;
      bulkDeleteLineas.mutate(Array.from(selectedLineaIds));
    }
  };

  // ─── Columnas ─── pestaña "Por pedido"
  const colsPedido: Column<PedidoClienteRow>[] = useMemo(() => [
    { key: 'fecha_pedido', label: 'Fecha pedido', render: (r) => fmt.date(r.fecha_pedido), sortValue: (r) => r.fecha_pedido },
    { key: 'created_at', label: 'Alta', render: (r) => <span className="text-xs text-slate-500">{fmt.date(r.created_at)}</span>, sortValue: (r) => r.created_at },
    {
      key: 'numero_cliente',
      label: `Nº ${clienteCodigo}`,
      render: (r) => {
        const abierto = desplegados.has(r.id);
        return (
          <span className="inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleDesplegado(r.id); }}
              className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded border border-slate-300 bg-white font-mono text-xs font-bold leading-none text-slate-600 hover:border-accent-400 hover:text-accent-700"
              title={abierto ? 'Ocultar las líneas del pedido' : 'Ver las líneas del pedido'}
              aria-expanded={abierto}
            >
              {abierto ? '−' : '+'}
            </button>
            <span className="font-mono text-xs">{r.numero_cliente ?? '—'}</span>
          </span>
        );
      },
      sortValue: (r) => r.numero_cliente ?? '',
    },
    { key: 'numero_approx', label: 'Nº APPROX', render: (r) => <span className="font-mono text-xs">{r.numero_approx ?? '—'}</span>, sortValue: (r) => r.numero_approx ?? '' },
    { key: 'pi_proveedor', label: 'PI proveedor', sortValue: (r) => r.pi_proveedor ?? '' },
    { key: 'lineas_count', label: 'Líneas', align: 'right', render: (r) => `${r.lineas_count}`, sortValue: (r) => r.lineas_count },
    { key: 'unidades_total', label: 'Uds.', align: 'right', render: (r) => fmt.int(r.unidades_total), sortValue: (r) => r.unidades_total },
    { key: 'unidades_entregadas_total', label: 'Entregadas', align: 'right', render: (r) => r.unidades_entregadas_total > 0 ? <span className="text-emerald-700">{fmt.int(r.unidades_entregadas_total)}</span> : <span className="text-slate-300">0</span>, sortValue: (r) => r.unidades_entregadas_total },
    { key: 'subtotal_eur', label: 'Importe', align: 'right', render: (r) => fmt.eur(r.subtotal_eur), sortValue: (r) => Number(r.subtotal_eur ?? 0) },
    { key: 'archivos_count', label: 'Docs', align: 'right', render: (r) => r.archivos_count > 0 ? `📎 ${r.archivos_count}` : <span className="text-slate-300">—</span>, sortValue: (r) => r.archivos_count },
    { key: 'estado', label: 'Estado', render: (r) => <Badge color={r.estado_color ?? '#64748b'}>{r.estado_nombre ?? r.estado}</Badge>, sortValue: (r) => r.estado_nombre ?? r.estado },
    { key: 'fecha_pedido_confirmado', label: 'Confirmado', render: (r) => fmt.date(r.fecha_pedido_confirmado), sortValue: (r) => r.fecha_pedido_confirmado ?? '' },
    {
      key: 'fecha_entrega_solicitada_pedido',
      label: 'Entrega solic.',
      render: (r) => r.fecha_entrega_solicitada_pedido
        ? <span className="text-xs">{fmt.date(r.fecha_entrega_solicitada_pedido)}</span>
        : <span className="text-xs text-slate-300">—</span>,
      sortValue: (r) => r.fecha_entrega_solicitada_pedido ?? '',
    },
    {
      key: 'etd_pedido',
      label: 'ETD',
      render: (r) => r.etd_pedido ? <span className="text-xs">{fmt.date(r.etd_pedido)}</span> : <span className="text-xs text-slate-300">—</span>,
      sortValue: (r) => r.etd_pedido ?? '',
    },
    {
      key: 'eta_pedido',
      label: 'ETA',
      render: (r) => r.eta_pedido ? <span className="text-xs">{fmt.date(r.eta_pedido)}</span> : <span className="text-xs text-slate-300">—</span>,
      sortValue: (r) => r.eta_pedido ?? '',
    },
    {
      key: 'ultima_entrega_fecha',
      label: 'Última entrega',
      render: (r) => r.ultima_entrega_fecha
        ? <span className="text-xs">{fmt.date(r.ultima_entrega_fecha)}</span>
        : <span className="text-xs text-slate-300">—</span>,
      sortValue: (r) => r.ultima_entrega_fecha ?? '',
    },
    {
      key: 'entregar',
      label: '',
      sortable: false,
      align: 'right',
      // «Entregar pedido» (CEO, 26/10/2026): pedidos confirmados con unidades pendientes.
      render: (r) => (ESTADOS_ENTREGABLES.has(r.estado) && Number(r.unidades_entregadas_total ?? 0) < Number(r.unidades_total ?? 0)
        ? (
          <Button
            size="sm"
            variant="create"
            onClick={(e) => { e.stopPropagation(); setEntregarPedido(r); }}
            title="Registra la entrega del pedido: todo lo pendiente o parcial por línea, con su fecha."
          >
            📦 Entregar pedido
          </Button>
        )
        : null),
    },
  ], [clienteCodigo, desplegados, toggleDesplegado]);

  // ─── Columnas ─── pestaña principal "Por línea de producto"
  const colsLinea: Column<LineaRow>[] = useMemo(() => [
    { key: 'fecha_pedido', label: 'Fecha pedido', render: (r) => fmt.date(r.fecha_pedido), sortValue: (r) => r.fecha_pedido },
    { key: 'numero_cliente', label: `Nº ${clienteCodigo}`, render: (r) => <span className="font-mono text-xs">{r.numero_cliente ?? '—'}</span>, sortValue: (r) => r.numero_cliente ?? '' },
    {
      key: 'sku',
      label: 'SKU',
      render: (r) => <span className="font-mono text-xs">{r.sku_cliente ?? r.producto_sku ?? '—'}</span>,
      sortValue: (r) => r.sku_cliente ?? r.producto_sku ?? '',
    },
    {
      key: 'nombre',
      label: 'Nombre',
      render: (r) => <span className="text-xs">{r.producto_nombre ?? r.descripcion ?? '—'}</span>,
      sortValue: (r) => r.producto_nombre ?? r.descripcion ?? '',
    },
    { key: 'subcategoria_nombre', label: 'Subtipo', render: (r) => r.subcategoria_nombre ?? <span className="text-slate-300">—</span>, sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'unidades', label: 'Unidades', align: 'right', render: (r) => fmt.int(r.unidades), sortValue: (r) => r.unidades },
    {
      key: 'unidades_entregadas',
      label: 'Entregadas',
      align: 'right',
      render: (r) => {
        const eUds = Number(r.unidades_entregadas ?? 0);
        const exceso = eUds > r.unidades;
        return (
          <span className={`font-mono text-xs ${exceso ? 'text-amber-700 font-semibold' : eUds > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
            {fmt.int(eUds)}
          </span>
        );
      },
      sortValue: (r) => Number(r.unidades_entregadas ?? 0),
    },
    {
      key: 'unidades_pendientes',
      label: 'Pendientes',
      align: 'right',
      render: (r) => {
        const p = Number(r.unidades_pendientes ?? Math.max(0, r.unidades - (r.unidades_entregadas ?? 0)));
        return (
          <span className={`font-mono text-xs ${p === 0 ? 'text-emerald-700 font-semibold' : 'text-rose-700'}`}>
            {fmt.int(p)}
          </span>
        );
      },
      sortValue: (r) => Number(r.unidades_pendientes ?? Math.max(0, r.unidades - (r.unidades_entregadas ?? 0))),
    },
    { key: 'precio_unitario', label: 'Importe', align: 'right', render: (r) => fmt.eur(r.precio_unitario), sortValue: (r) => Number(r.precio_unitario ?? 0) },
    {
      key: 'subtotal',
      label: 'Subtotal',
      align: 'right',
      render: (r) => {
        const uds = Math.max(r.unidades, r.unidades_entregadas ?? 0);
        return <span className="font-semibold">{fmt.eur((Number(r.precio_unitario) || 0) * uds)}</span>;
      },
      sortValue: (r) => (Number(r.precio_unitario) || 0) * Math.max(r.unidades, r.unidades_entregadas ?? 0),
    },
    { key: 'fecha_entrega_solicitada', label: 'Entrega', render: (r) => fmt.date(r.fecha_entrega_solicitada), sortValue: (r) => r.fecha_entrega_solicitada ?? '' },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) => <Badge color={r.estado_color ?? '#64748b'}>{r.estado_nombre ?? r.estado}</Badge>,
      sortValue: (r) => r.estado_nombre ?? r.estado,
    },
    { key: 'etd', label: 'ETD', render: (r) => fmt.date(r.etd), sortValue: (r) => r.etd ?? '' },
    { key: 'eta', label: 'ETA', render: (r) => fmt.date(r.eta), sortValue: (r) => r.eta ?? '' },
    {
      key: 'ultima_entrega_fecha',
      label: 'Última entrega',
      render: (r) => r.ultima_entrega_fecha
        ? <span className="text-xs">{fmt.date(r.ultima_entrega_fecha)}</span>
        : <span className="text-xs text-slate-300">—</span>,
      sortValue: (r) => r.ultima_entrega_fecha ?? '',
    },
    {
      key: 'acciones',
      label: '',
      sortable: false,
      align: 'right',
      render: (r) => {
        // Si la línea (producto) aún no está aceptada Y el pedido sigue PDTE de
        // respuesta, la acción es ACEPTAR ESTE producto (genera su pedido a
        // proveedor + alta del producto). En cuanto el pedido pasa a ACEPTADO o
        // posterior, el botón "Marcar aceptado" desaparece (aunque la línea no
        // tenga el flag, p.ej. tras una aceptación en masa).
        if (!r.aceptada && r.estado === 'PDTE_RESPUESTA') {
          return (
            <Button
              size="sm"
              variant="create"
              onClick={async (e) => {
                e.stopPropagation();
                if (!(await confirmar({ title: '¿Aceptar este producto?', message: 'Se dará de alta en el catálogo del cliente (heredando la taxonomía de la cotización) y se generará su pedido a proveedor. No afecta a las demás líneas.', confirmLabel: 'Aceptar producto' }))) return;
                marcarAceptado.mutate({ pedidoClienteId: r.pedido_cliente_id, lineaId: r.linea_id });
              }}
              disabled={marcarAceptado.isPending}
              title="Acepta este producto: crea el producto en el catálogo del cliente y genera su pedido a proveedor."
            >
              ✓ {marcarAceptado.isPending ? 'Aceptando…' : 'Marcar aceptado'}
            </Button>
          );
        }
        const pend = Number(r.unidades_pendientes ?? Math.max(0, r.unidades - (r.unidades_entregadas ?? 0)));
        const done = pend === 0 && Number(r.unidades_entregadas ?? 0) > 0;
        return (
          <Button
            size="sm"
            variant={done ? 'secondary' : 'create'}
            onClick={(e) => { e.stopPropagation(); abrirEntrega(r); }}
            title={done ? 'Marcar entrega adicional (sobre-entrega)' : 'Marcar unidades entregadas de esta línea'}
          >
            ✓ {done ? 'Más' : 'Entregar'}
          </Button>
        );
      },
    },
  ], [abrirEntrega, marcarAceptado]);

  // Por ahora solo GAME tiene plantilla de PDF soportada.
  const supportsPdfImport = clienteCodigo === 'GAME';

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {supportsPdfImport && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExcelHelpOpen(true)}
                title="Ver columnas, nombres y orden esperados por el importador Excel."
              >
                ℹ️ Formato Excel
              </Button>
            )}
            {esTicnova && (
              <ZonaImport
                compacta
                icono="⬆️"
                titulo="Importar pedido PDF"
                hint="Pedido de compra de TICNOVA (COM/…) en PDF. Verás las líneas antes de guardar; el pedido se crea en Pdte. respuesta y el PDF queda adjunto como «Pedido cliente a APPROX»."
                busy={previewPdf.isPending || confirmarImport.isPending}
                busyLabel="Procesando PDF…"
                onClick={() => fileInputRef.current?.click()}
                onFile={(f) => {
                  if (!/\.pdf$/i.test(f.name)) { setImportError(`"${f.name}" no es un PDF. Arrastra aquí el pedido de TICNOVA en PDF.`); return; }
                  previewPdf.mutate(f);
                }}
              />
            )}
            <Button variant="create" onClick={() => setNuevoOpen(true)} disabled={!clienteId}>+ Nuevo pedido</Button>
          </>
        }
      />

      {(supportsPdfImport || esTicnova) && (
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) previewPdf.mutate(file);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      )}

      {supportsPdfImport && (
        <>
          <input
            ref={fileInputExcelRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) previewExcel.mutate(file);
              if (fileInputExcelRef.current) fileInputExcelRef.current.value = '';
            }}
          />
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <ZonaImport
              icono="⬆️"
              titulo="Importar PDF GAME"
              hint="Importar pedido desde PDF de GAME (Propuesta de pedido). Verás una previsualización antes de guardar. El PDF se adjunta automáticamente como documento Pedido GAME."
              busy={previewPdf.isPending || confirmarImport.isPending}
              busyLabel="Procesando PDF…"
              onClick={() => fileInputRef.current?.click()}
              onFile={(f) => {
                if (!/\.pdf$/i.test(f.name)) { setImportError(`"${f.name}" no es un PDF. Arrastra aquí la Propuesta de pedido de GAME en PDF.`); return; }
                previewPdf.mutate(f);
              }}
            />
            <ZonaImport
              icono="📊"
              titulo="Importar Excel"
              hint="Importar pedido(s) desde Excel. Pulsa 'Formato Excel' para ver la plantilla de columnas esperadas."
              busy={previewExcel.isPending || confirmarExcel.isPending}
              busyLabel="Procesando Excel…"
              onClick={() => fileInputExcelRef.current?.click()}
              onFile={(f) => {
                if (!/\.(xlsx|xls)$/i.test(f.name)) { setImportError(`"${f.name}" no es un Excel (.xlsx/.xls).`); return; }
                previewExcel.mutate(f);
              }}
            />
          </div>
        </>
      )}

      {importError && (
        <div className="mb-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <strong className="font-semibold">No se pudo importar:</strong> {importError}{' '}
          <button onClick={() => setImportError(null)} className="ml-2 cursor-pointer underline">cerrar</button>
        </div>
      )}
      {importWarning && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <strong className="font-semibold">Pedido creado con aviso:</strong> {importWarning}{' '}
          <button onClick={() => setImportWarning(null)} className="ml-2 cursor-pointer underline">cerrar</button>
        </div>
      )}

      <Tabs
        tabs={[
          { key: 'productos', label: 'Por línea de producto' },
          { key: 'pedidos', label: 'Por pedido' },
        ]}
        active={tab}
        onChange={(k) => setTab(k as 'productos' | 'pedidos')}
      />

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Estado:</span>
          <Select
            options={ESTADOS}
            value={estado}
            emptyLabel={null}
            onChange={(e) => setEstado(e.target.value)}
            className="w-48 text-sm"
          />
        </div>
        {tab === 'productos' && (
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Buscar por SKU, EAN, Nº ${clienteCodigo}, Nº APPROX, descripción…`}
                className="w-full max-w-xl"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="cursor-pointer text-xs text-slate-500 hover:underline"
                >
                  limpiar
                </button>
              )}
            </div>
            <p className="mt-0.5 text-[10px] leading-tight text-slate-400">
              Varias palabras = todas deben coincidir · <span className="font-medium">palabra1 / palabra2</span> = cualquiera coincide · <span className="font-medium">"frase exacta"</span> = coincidencia literal
            </p>
          </div>
        )}
      </div>

      {bulkError && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <span className="text-base">⚠️</span>
          <div className="flex-1">
            <p className="font-semibold">No se pudo cambiar el estado.</p>
            <p>{bulkError}</p>
          </div>
          <button onClick={() => setBulkError(null)} className="cursor-pointer text-xs underline">cerrar</button>
        </div>
      )}

      {((tab === 'pedidos' && selectedIds.size > 0)
        || (tab === 'productos' && selectedLineaIds.size > 0)) && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm">
          <span className="font-semibold text-sky-900">
            {tab === 'pedidos'
              ? `${selectedIds.size} pedido${selectedIds.size === 1 ? '' : 's'} seleccionado${selectedIds.size === 1 ? '' : 's'}`
              : `${selectedLineaIds.size} línea${selectedLineaIds.size === 1 ? '' : 's'} (${pedidoIdsDesdeLineas.size} pedido${pedidoIdsDesdeLineas.size === 1 ? '' : 's'} único${pedidoIdsDesdeLineas.size === 1 ? '' : 's'})`}
          </span>
          <div className="flex-1" />
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Cambiar estado a</label>
            <Select
              options={ESTADOS.filter((e) => e.value)}
              value={bulkEstado}
              onChange={(e) => setBulkEstado(e.target.value)}
              emptyLabel="(elige)"
              className="w-56"
            />
          </div>
          <Button variant="save" onClick={applyBulk} disabled={!bulkEstado || bulkMut.isPending}>
            {bulkMut.isPending ? 'Aplicando…' : 'Aplicar'}
          </Button>
          <Button
            variant="edit"
            onClick={() => setFechasBulkOpen(true)}
            title={tab === 'pedidos'
              ? 'Cambiar fecha de entrega, ETD o ETA en TODAS las líneas de los pedidos seleccionados.'
              : 'Cambiar fecha de entrega, ETD o ETA en bloque para las líneas seleccionadas.'}
          >
            📅 Fechas
          </Button>
          {tab === 'pedidos' && selectedIds.size >= 2 && (
            <Button
              variant="edit"
              onClick={() => setFusionarOpen(true)}
              title="Juntar los pedidos seleccionados en uno solo con todas sus líneas."
            >
              🔗 Fusionar
            </Button>
          )}
          <Button
            variant="danger"
            onClick={deleteBulkHandler}
            disabled={bulkDeletePedidos.isPending || bulkDeleteLineas.isPending}
            title={tab === 'pedidos'
              ? 'Soft delete: los pedidos se ocultan pero quedan en el audit log.'
              : 'Las líneas se eliminan definitivamente (no tienen soft delete).'}
          >
            🗑 {(bulkDeletePedidos.isPending || bulkDeleteLineas.isPending) ? 'Eliminando…' : 'Eliminar'}
          </Button>
          <button
            type="button"
            onClick={() => { setSelectedIds(new Set()); setSelectedLineaIds(new Set()); }}
            className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
          >
            Limpiar selección
          </button>
        </div>
      )}

      {tab === 'productos' ? (
        <DataTable<LineaRow>
          columns={colsLinea}
          rows={lineas.data?.data ?? []}
          loading={lineas.isLoading}
          rowKey={(r) => r.linea_id}
          onRowClick={(r) => navigate(`${base}/pedidos/${r.pedido_cliente_id}?focus=${r.linea_id}`)}
          defaultSort={{ key: 'fecha_pedido', desc: true }}
          selectable
          selectedIds={selectedLineaIds}
          onSelectionChange={setSelectedLineaIds}
          searchable={false}
          storageKey="lineas"
        />
      ) : (
        <DataTable<PedidoClienteRow>
          columns={colsPedido}
          rows={cabeceras.data?.data ?? []}
          loading={cabeceras.isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`${base}/pedidos/${r.id}`)}
          defaultSort={{ key: 'fecha_pedido', desc: true }}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          storageKey="cabeceras"
          expandedIds={desplegados}
          // Las líneas empiezan justo bajo el texto del número (a la derecha del
          // +/−: botón de 1.25rem + hueco de 0.375rem).
          expandedAlign={{ key: 'numero_cliente', indent: '1.625rem' }}
          renderExpanded={(r) => (
            <LineasPedidoDesplegadas
              pedidoId={r.id}
              clienteCodigo={clienteCodigo}
              onAbrirLinea={(lineaId) => navigate(`${base}/pedidos/${r.id}?focus=${lineaId}`)}
            />
          )}
        />
      )}

      {fusionarOpen && (
        <FusionarPedidosModal
          pedidos={(cabeceras.data?.data ?? []).filter((p) => selectedIds.has(p.id))}
          onClose={() => setFusionarOpen(false)}
          onDone={() => { setFusionarOpen(false); setSelectedIds(new Set()); }}
        />
      )}

      {entregarPedido && (
        <EntregarPedidoModal
          pedidoId={entregarPedido.id}
          titulo={entregarPedido.numero_cliente ?? entregarPedido.numero_approx ?? fmt.date(entregarPedido.fecha_pedido)}
          onClose={() => setEntregarPedido(null)}
        />
      )}

      <Modal
        open={!!entregadoIds}
        onClose={() => setEntregadoIds(null)}
        title="Marcar como ENTREGADO"
        size="sm"
        footer={
          <>
            <Button variant="danger" onClick={() => setEntregadoIds(null)} disabled={bulkMut.isPending}>Cancelar</Button>
            <Button variant="save" onClick={confirmarEntregadoBulk} disabled={!entregadoFecha || bulkMut.isPending}>
              {bulkMut.isPending ? 'Guardando…' : 'Marcar entregado'}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          {entregadoIds?.length === 1 ? '1 pedido' : `${entregadoIds?.length ?? 0} pedidos`}. Indica la fecha en que se entregó: queda registrada como «Última entrega».
        </p>
        <FormRow label="Fecha de entrega">
          <Input type="date" value={entregadoFecha} max={hoyLocal()} onChange={(e) => setEntregadoFecha(e.target.value)} autoFocus />
        </FormRow>
      </Modal>

      {/* Modal preview de import PDF GAME — los datos no se guardan
          hasta que el CEO pulsa "OK y guardar". */}
      <Modal
        open={!!previewData}
        onClose={cancelarImport}
        title={`Previsualización · Pedido ${clienteCodigo} desde PDF`}
        size="xl"
        footer={
          <>
            <Button variant="danger" onClick={cancelarImport} disabled={confirmarImport.isPending}>
              Descartar
            </Button>
            <Button
              variant="save"
              onClick={() => confirmarImport.mutate()}
              disabled={confirmarImport.isPending || previewData?.verificacion?.accion === 'nada'}
              title={previewData?.verificacion?.accion === 'nada' ? 'El pedido ya está registrado con su documento: no hay nada que importar.' : undefined}
            >
              {confirmarImport.isPending
                ? 'Guardando…'
                : previewData?.verificacion?.accion === 'adjuntar'
                  ? '📎 Adjuntar PDF al pedido'
                  : previewData?.verificacion?.accion === 'nada' ? 'Ya importado' : '✓ OK y guardar'}
            </Button>
          </>
        }
      >
        {previewData && (
          <ImportPreviewBody preview={previewData} fileName={previewFile?.name ?? null} clienteCodigo={clienteCodigo} base={base} />
        )}
      </Modal>

      {/* Modal preview de import Excel — agrupado por número de pedido. */}
      <Modal
        open={!!excelPreview}
        onClose={cancelarExcel}
        title="Previsualización · Pedidos desde Excel"
        size="xl"
        footer={
          <>
            <Button variant="danger" onClick={cancelarExcel} disabled={confirmarExcel.isPending}>
              Descartar
            </Button>
            <Button
              variant="save"
              onClick={() => confirmarExcel.mutate()}
              disabled={confirmarExcel.isPending}
            >
              {confirmarExcel.isPending ? 'Guardando…' : '✓ OK y guardar'}
            </Button>
          </>
        }
      >
        {excelPreview && (
          <ExcelImportPreviewBody
            preview={excelPreview}
            fileName={excelFile?.name ?? null}
            decisiones={excelDecisiones}
            onDecision={(numero, decision) => setExcelDecisiones((d) => ({ ...d, [numero]: decision }))}
          />
        )}
      </Modal>

      {/* Modal de ayuda: formato Excel esperado por el importador. */}
      <Modal
        open={excelHelpOpen}
        onClose={() => setExcelHelpOpen(false)}
        title="Formato Excel para importador de pedidos"
        size="xl"
        footer={
          <Button variant="secondary" onClick={() => setExcelHelpOpen(false)}>Cerrar</Button>
        }
      >
        <ExcelFormatHelp />
      </Modal>

      {/* Modal: motivo de cancelación bulk. */}
      <Modal
        open={cancelacionOpen}
        onClose={() => setCancelacionOpen(false)}
        title={`Cancelar ${cancelacionIds.length} pedido(s)`}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelacionOpen(false)} disabled={bulkMut.isPending}>Volver</Button>
            <Button variant="danger" onClick={confirmarCancelacionBulk} disabled={bulkMut.isPending}>
              {bulkMut.isPending ? 'Cancelando…' : '🚫 Confirmar cancelación'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
            <p className="font-semibold">Se cancelarán {cancelacionIds.length} pedido(s).</p>
            {tab === 'productos' && (
              <p className="mt-1">Habías seleccionado <strong>{selectedLineaIds.size} línea(s)</strong>. Recuerda que el estado vive a nivel pedido — se cancelará el pedido completo de cada línea seleccionada.</p>
            )}
            <p className="mt-1">Las líneas no se borran. El motivo queda registrado en cada pedido cancelado.</p>
          </div>
          <FormRow label="Motivo de la cancelación">
            <textarea
              rows={3}
              value={cancelacionMotivo}
              onChange={(e) => setCancelacionMotivo(e.target.value)}
              placeholder="Ej: Cliente cancela, cambio de modelo, sin stock, etc."
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </FormRow>
        </div>
      </Modal>

      {/* Modal: bulk update de fechas en líneas. */}
      <Modal
        open={fechasBulkOpen}
        onClose={() => setFechasBulkOpen(false)}
        title={tab === 'pedidos'
          ? `Cambiar fechas en ${selectedIds.size} pedido(s) (afecta a TODAS sus líneas)`
          : `Cambiar fechas en ${selectedLineaIds.size} línea(s)`}
        size="md"
        footer={
          <>
            <Button variant="danger" onClick={() => setFechasBulkOpen(false)} disabled={fechasBulkMut.isPending}>Cancelar</Button>
            <Button
              variant="save"
              onClick={() => fechasBulkMut.mutate()}
              disabled={
                fechasBulkMut.isPending
                || (!fechasDraft.fecha_entrega_solicitada && !fechasDraft.etd && !fechasDraft.eta)
              }
            >
              {fechasBulkMut.isPending ? 'Aplicando…' : 'Aplicar fechas'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">
            Rellena las fechas que quieras aplicar. Los campos vacíos se ignoran (no pisan el valor actual).
            {tab === 'pedidos' && ' Las fechas se escriben en TODAS las líneas de los pedidos seleccionados.'}
          </p>
          <FormRow label="Fecha de entrega solicitada">
            <Input
              type="date"
              value={fechasDraft.fecha_entrega_solicitada}
              onChange={(e) => setFechasDraft((d) => ({ ...d, fecha_entrega_solicitada: e.target.value }))}
            />
          </FormRow>
          <FormRow label="ETD (salida de fábrica)">
            <Input
              type="date"
              value={fechasDraft.etd}
              onChange={(e) => setFechasDraft((d) => ({ ...d, etd: e.target.value }))}
            />
          </FormRow>
          <FormRow label="ETA (llegada estimada)">
            <Input
              type="date"
              value={fechasDraft.eta}
              onChange={(e) => setFechasDraft((d) => ({ ...d, eta: e.target.value }))}
            />
          </FormRow>
        </div>
      </Modal>

      {/* Modal: marcar entrega de una línea. */}
      <Modal
        open={!!entregaLinea}
        onClose={cerrarEntrega}
        title="Marcar unidades entregadas"
        size="md"
        footer={
          <>
            <Button variant="danger" onClick={cerrarEntrega} disabled={entregar.isPending}>Cancelar</Button>
            <Button
              variant="save"
              onClick={() => entregar.mutate({ confirmar: false })}
              disabled={entregar.isPending || !entregaUds || Number(entregaUds) <= 0}
            >
              {entregar.isPending ? 'Guardando…' : '✓ Marcar entregado'}
            </Button>
          </>
        }
      >
        {entregaLinea && (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
              <div><span className="font-semibold">Producto:</span> {entregaLinea.producto_nombre ?? entregaLinea.descripcion ?? '—'}</div>
              <div>
                <span className="font-semibold">Previstas:</span> {fmt.int(entregaLinea.unidades)} ·{' '}
                <span className="font-semibold">Entregadas hasta ahora:</span> {fmt.int(entregaLinea.unidades_entregadas ?? 0)} ·{' '}
                <span className="font-semibold">Pendientes:</span> {fmt.int(entregaLinea.unidades_pendientes ?? entregaLinea.unidades)}
              </div>
            </div>
            <FormRow label="Unidades a marcar como entregadas en esta operación">
              <Input
                type="number"
                min={1}
                value={entregaUds}
                onChange={(e) => setEntregaUds(e.target.value)}
                placeholder="Ej: 50"
              />
            </FormRow>
            <FormRow label="Fecha de la entrega" hint="Por defecto hoy. Puedes cambiarlo si la entrega fue otro día.">
              <Input
                type="date"
                value={entregaFecha}
                onChange={(e) => setEntregaFecha(e.target.value)}
              />
            </FormRow>
            <FormRow label="Notas (opcional)">
              <Input
                value={entregaNotas}
                onChange={(e) => setEntregaNotas(e.target.value)}
                placeholder="Ej: Entregado por mensajero MRW guía 12345"
              />
            </FormRow>
            <p className="text-xs text-slate-500">
              Cada entrega queda registrada con su fecha y unidades. Si el total entregado supera las unidades previstas se considera <strong>sobre-entrega</strong>:
              te lo confirmaremos antes de aplicar y cerrará el pedido como <strong>FINALIZADO</strong>.
            </p>
            {entregaError && (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800">{entregaError}</div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={nuevoOpen}
        onClose={() => setNuevoOpen(false)}
        title={`Nuevo pedido ${clienteCodigo}`}
        footer={
          <>
            <Button variant="danger" onClick={() => setNuevoOpen(false)}>Cancelar</Button>
            <Button variant="create" onClick={() => crear.mutate()} disabled={!clienteId || crear.isPending}>Crear</Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormRow label="Fecha del pedido"><Input type="date" value={draft.fecha_pedido} onChange={(e) => setDraft((d) => ({ ...d, fecha_pedido: e.target.value }))} /></FormRow>
          <FormRow label="Nº pedido APPROX (opcional)"><Input value={draft.numero_approx} onChange={(e) => setDraft((d) => ({ ...d, numero_approx: e.target.value }))} placeholder="Editable después" /></FormRow>
          <FormRow label="Nº pedido CLIENTE (opcional)"><Input value={draft.numero_cliente} onChange={(e) => setDraft((d) => ({ ...d, numero_cliente: e.target.value }))} /></FormRow>
          <p className="text-xs text-slate-500">El pedido se creará en estado <em>Pdte. respuesta</em>. Después podrás añadir líneas eligiendo cotizaciones.</p>
        </div>
      </Modal>
    </div>
  );
}

function ImportPreviewBody({ preview, fileName, clienteCodigo, base }: { preview: ImportPreview; fileName: string | null; clienteCodigo: string; base: string }) {
  const { parsed, matches, verificacion } = preview;
  const crear = !verificacion || verificacion.accion === 'crear';
  const matchById = new Map<string, ImportPreviewMatch>();
  for (const m of matches) matchById.set(m.sku, m);
  const totalUds = parsed.lineas.reduce((acc, l) => acc + (l.unidades || 0), 0);
  const totalImporte = parsed.lineas.reduce(
    (acc, l) => acc + (Number(l.precio_unitario_eur) || 0) * (l.unidades || 0),
    0,
  );
  const unmatched = matches.filter((m) => !m.producto_id).length;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label={`Nº pedido ${clienteCodigo}`} value={parsed.numero_pedido_game ?? '—'} mono />
        <Field label="Fecha pedido" value={fmt.date(parsed.fecha_pedido)} />
        <Field label="Líneas" value={String(parsed.lineas.length)} />
        <Field label="Unidades" value={fmt.int(totalUds)} />
      </div>
      {verificacion && <VerificacionPedido v={verificacion} base={base} />}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        {parsed.su_referencia && <p className="mb-1">Su referencia: <strong>{parsed.su_referencia}</strong> (se guarda en las notas del pedido).</p>}
        <p>
          {fileName ? <>Fichero: <span className="font-mono">{fileName}</span>. </> : null}
          Importe total estimado: <strong>{fmt.eur(totalImporte)}</strong>.{' '}
          {!crear ? null : unmatched > 0
            ? <span className="text-sky-700">{unmatched} SKU{unmatched === 1 ? '' : 's'} no existe{unmatched === 1 ? '' : 'n'} en el catálogo — se dará{unmatched === 1 ? '' : 'n'} de alta como producto {clienteCodigo} al confirmar{clienteCodigo === 'TICNOVA' ? ' (marca por el prefijo, clasificado y marcado para revisar)' : ''}.</span>
            : <span className="text-emerald-700">Todos los SKU encontrados en el catálogo.</span>}
        </p>
        {crear && <p className="mt-1 text-slate-500">El PDF se adjuntará automáticamente al pedido como documento <em>Pedido cliente a APPROX</em>.{clienteCodigo === 'TICNOVA' ? ' El pedido se crea en Pdte. respuesta: al aceptarlo se generan los pedidos a proveedor.' : ''}</p>}
      </div>
      {/* Descuadre contra el TOTALES del propio PDF: significa que el parser no
          ha entendido alguna fila. Sin este aviso el pedido se importaría
          incompleto sin que nada lo delatase. */}
      {parsed.avisos && parsed.avisos.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-semibold">Revisa antes de confirmar:</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {parsed.avisos.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <th className="px-2 py-1.5">SKU</th>
              <th className="px-2 py-1.5">Producto en BD</th>
              <th className="px-2 py-1.5">Descripción PDF</th>
              <th className="px-2 py-1.5 text-right">Uds.</th>
              <th className="px-2 py-1.5 text-right">€/ud</th>
              <th className="px-2 py-1.5 text-right">Subtotal</th>
              <th className="px-2 py-1.5">Entrega</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {parsed.lineas.map((l, idx) => {
              const m = matchById.get(l.sku);
              const subtotal = (Number(l.precio_unitario_eur) || 0) * (l.unidades || 0);
              return (
                <tr key={`${l.sku}-${idx}`} className="hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-mono">{l.sku}</td>
                  <td className="px-2 py-1.5">
                    {m?.producto_id
                      ? <span className="text-emerald-700">✓ {m.producto_nombre ?? '—'}</span>
                      : <span className="text-sky-700">⊕ se creará</span>}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">{l.producto ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right">{fmt.int(l.unidades)}</td>
                  <td className="px-2 py-1.5 text-right">{fmt.eur(l.precio_unitario_eur)}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{fmt.eur(subtotal)}</td>
                  <td className="px-2 py-1.5">{fmt.date(l.entrega_estimada)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TEXTO_DOCUMENTO: Record<NonNullable<Verificacion['documento']>, string> = {
  identico: 'es exactamente el mismo fichero que este',
  mismo_contenido: 'tiene el mismo contenido que este (mismo nº y mismas líneas)',
  distinto: 'es DISTINTO de este: no se sustituye; si el bueno es este, súbelo a mano en la ficha del pedido',
  no_comprobado: 'no se ha podido comparar con este',
};

/** Qué encontró la verificación previa y qué hará el botón de guardar. */
function VerificacionPedido({ v, base }: { v: Verificacion; base: string }) {
  if (v.accion === 'crear' || !v.existente) {
    return (
      <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-800">
        <p className="font-semibold">Pedido nuevo: no hay ninguno registrado con este número. Al guardar se crea con sus líneas y el PDF.</p>
      </div>
    );
  }
  const c = v.comparacion;
  const tono = v.accion === 'adjuntar' ? 'border-sky-300 bg-sky-50 text-sky-900' : 'border-slate-300 bg-slate-100 text-slate-800';
  return (
    <div className={`rounded-md border p-3 text-xs ${tono}`}>
      <p className="font-semibold">
        Este pedido ya está registrado como {v.existente.numero_cliente} ({v.existente.estado.replace(/_/g, ' ').toLowerCase()}).{' '}
        <Link to={`${base}/pedidos/${v.existente.id}`} className="font-normal underline">Abrir el pedido</Link>
      </p>
      {v.accion === 'adjuntar'
        ? <p className="mt-1">No tiene documento: al pulsar «Adjuntar PDF al pedido» solo se le adjunta este PDF. No se crea otro pedido ni se tocan sus líneas.</p>
        : <p className="mt-1">Ya tiene documento ({v.documento_existente?.nombre}), que {TEXTO_DOCUMENTO[v.documento ?? 'no_comprobado']}. No se cambia nada.</p>}
      {c && (c.coincide
        ? <p className="mt-1 text-emerald-700">✓ Las líneas coinciden con el documento: {c.registrado.lineas} líneas, {fmt.int(c.registrado.unidades)} uds y {fmt.eur(c.registrado.importe)}.</p>
        : (
          <div className="mt-1 text-amber-800">
            <p className="font-semibold">El pedido registrado no coincide con el documento:</p>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4">{c.diferencias.map((d, i) => <li key={i}>{d}</li>)}</ul>
            <p className="mt-0.5">Documento: {c.documento.lineas} líneas, {fmt.int(c.documento.unidades)} uds, {fmt.eur(c.documento.importe)} · Registrado: {c.registrado.lineas} líneas, {fmt.int(c.registrado.unidades)} uds, {fmt.eur(c.registrado.importe)}. No se corrige solo: revísalo en la ficha del pedido.</p>
          </div>
        ))}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function ExcelImportPreviewBody({
  preview, fileName, decisiones, onDecision,
}: {
  preview: ExcelImportPreview;
  fileName: string | null;
  decisiones: Record<string, ExcelDecision>;
  onDecision: (numero: string, decision: ExcelDecision) => void;
}) {
  const { grupos, total_filas } = preview.parsed;
  const detectados = Object.keys(preview.headers_detectados);
  const totalUnmatched = grupos.reduce(
    (acc, g) => acc + g.lineas.filter((l) => !l.producto_id && l.sku).length,
    0,
  );
  const totalSinSku = grupos.reduce(
    (acc, g) => acc + g.lineas.filter((l) => !l.sku).length,
    0,
  );
  const totalImporte = grupos.reduce(
    (acc, g) => acc + g.lineas.reduce((a, l) => a + (Number(l.coste) || 0) * (l.unidades || 0), 0),
    0,
  );
  const duplicados = grupos.filter((g) => g.existente).length;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Pedidos detectados" value={String(grupos.length)} />
        <Field label="Líneas totales" value={String(total_filas)} />
        <Field label="Importe estimado" value={fmt.eur(totalImporte)} />
        <Field label="Fichero" value={fileName ?? '—'} mono />
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        <p>
          Columnas detectadas: <span className="font-mono">{detectados.join(', ')}</span>.
        </p>
        {duplicados > 0 && (
          <p className="mt-1 text-rose-700">
            🔁 {duplicados} pedido(s) con número ya existente en BD — decide abajo si sobrescribir o ignorar. Por defecto se ignoran.
          </p>
        )}
        {totalSinSku > 0 && (
          <p className="mt-1 text-amber-700">
            ⚠️ {totalSinSku} línea(s) sin SKU — se crearán igualmente pero el pedido necesitará SKU para avanzar de estado.
          </p>
        )}
        {totalUnmatched > 0 && (
          <p className="mt-1 text-sky-700">
            ⊕ {totalUnmatched} SKU(s) no existen en el catálogo — se darán de alta automáticamente como producto GAME al confirmar.
          </p>
        )}
        {totalSinSku === 0 && totalUnmatched === 0 && duplicados === 0 && (
          <p className="mt-1 text-emerald-700">Todos los SKU encontrados; sin duplicados.</p>
        )}
      </div>

      <div className="max-h-[55vh] space-y-3 overflow-y-auto">
        {grupos.map((g) => {
          const importe = g.lineas.reduce((a, l) => a + (Number(l.coste) || 0) * (l.unidades || 0), 0);
          const uds = g.lineas.reduce((a, l) => a + (l.unidades || 0), 0);
          const decision = decisiones[g.numero] ?? (g.existente ? 'ignorar' : 'crear');
          return (
            <div key={g.numero} className={`rounded-md border ${g.existente ? 'border-rose-200' : 'border-slate-200'}`}>
              <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 text-xs ${g.existente ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-slate-50'}`}>
                <span>
                  <span className="font-semibold text-slate-900">Pedido #{g.numero}</span>
                  <span className="ml-2 text-slate-500">{fmt.date(g.fecha_pedido)}</span>
                  {g.existente && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                      🔁 Ya existe ({g.existente.lineas_actuales} líneas · {g.existente.estado})
                    </span>
                  )}
                </span>
                <span className="font-mono text-slate-700">
                  {g.lineas.length} línea(s) · {fmt.int(uds)} uds · {fmt.eur(importe)}
                </span>
              </div>
              {g.existente && (
                <div className="flex flex-wrap items-center gap-3 border-b border-rose-200 bg-rose-50/50 px-3 py-2 text-xs">
                  <span className="font-semibold text-rose-900">¿Qué hacemos con este pedido?</span>
                  <label className="inline-flex cursor-pointer items-center gap-1.5">
                    <input
                      type="radio"
                      name={`decision-${g.numero}`}
                      checked={decision === 'ignorar'}
                      onChange={() => onDecision(g.numero, 'ignorar')}
                    />
                    <span>Ignorar (no tocar)</span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-1.5">
                    <input
                      type="radio"
                      name={`decision-${g.numero}`}
                      checked={decision === 'sobrescribir'}
                      onChange={() => onDecision(g.numero, 'sobrescribir')}
                    />
                    <span className="text-rose-800">Sobrescribir (borra las {g.existente.lineas_actuales} líneas actuales y mete las del Excel)</span>
                  </label>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-1.5">SKU</th>
                      <th className="px-2 py-1.5">Producto en BD</th>
                      <th className="px-2 py-1.5">Descripción Excel</th>
                      <th className="px-2 py-1.5">Ref/PN</th>
                      <th className="px-2 py-1.5 text-right">Uds.</th>
                      <th className="px-2 py-1.5 text-right">€/ud</th>
                      <th className="px-2 py-1.5 text-right">Subtotal</th>
                      <th className="px-2 py-1.5">ETA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {g.lineas.map((l, idx) => {
                      const subtotal = (Number(l.coste) || 0) * (l.unidades || 0);
                      return (
                        <tr key={`${g.numero}-${idx}`} className="hover:bg-slate-50">
                          <td className="px-2 py-1.5 font-mono">{l.sku ?? <span className="italic text-amber-700">sin SKU</span>}</td>
                          <td className="px-2 py-1.5">
                            {l.producto_id
                              ? <span className="text-emerald-700">✓ {l.producto_match_nombre ?? '—'}</span>
                              : l.sku
                                ? <span className="text-sky-700">⊕ se creará</span>
                                : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-slate-700">{l.producto ?? '—'}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px] text-slate-600">{l.ref ?? '—'}</td>
                          <td className="px-2 py-1.5 text-right">{fmt.int(l.unidades)}</td>
                          <td className="px-2 py-1.5 text-right">{fmt.eur(l.coste)}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{fmt.eur(subtotal)}</td>
                          <td className="px-2 py-1.5">{fmt.date(l.fecha_estimada)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Documentación inline del formato Excel esperado por el importador.
// El orden de las columnas NO es fijo (detectamos por cabecera); pero
// se sugiere el orden de abajo para que el CEO recuerde rápido.
// ─────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNAS: Array<{
  pos: number;
  nombre: string;
  obligatorio: boolean;
  aliases: string[];
  ejemplo: string;
  notas?: string;
}> = [
  { pos: 1, nombre: 'FECHA PEDIDO', obligatorio: false, aliases: ['Fecha', 'Fecha del pedido'], ejemplo: '25/05/2026', notas: 'dd/mm/yyyy o cualquier formato Excel. Si vacía, se usa la fecha de hoy.' },
  { pos: 2, nombre: 'PEDIDO', obligatorio: true, aliases: ['Nº pedido', 'Nº GAME', 'Numero pedido'], ejemplo: 'PR00056327', notas: 'Identificador del pedido cliente. Filas con el mismo valor se agrupan en un único pedido.' },
  { pos: 3, nombre: 'SKU', obligatorio: false, aliases: ['SKU cliente', 'Código SKU'], ejemplo: '258449', notas: 'Si el SKU no existe en BD, se crea automáticamente como producto GAME en EN_FABRICACION con coste = COSTE UNIDADES.' },
  { pos: 4, nombre: 'EAN', obligatorio: false, aliases: ['EAN13', 'Código EAN'], ejemplo: '8412345678901' },
  { pos: 5, nombre: 'REF/P/N', obligatorio: false, aliases: ['Ref', 'PN', 'Referencia'], ejemplo: 'MON27-IPS-FHD' },
  { pos: 6, nombre: 'PRODUCTO', obligatorio: false, aliases: ['Descripción', 'Nombre producto'], ejemplo: '27" IPS / FHD HW MONITOR', notas: 'Texto descriptivo de la línea.' },
  { pos: 7, nombre: 'COSTE UNIDADES', obligatorio: false, aliases: ['Coste unidad', 'Precio unidad', 'Precio unitario', 'Coste', 'Precio'], ejemplo: '85,42', notas: 'Importe en EUR por unidad. Se usa como precio_unitario de la línea.' },
  { pos: 8, nombre: 'UNIDADES', obligatorio: true, aliases: ['Uds', 'Cantidad'], ejemplo: '1000', notas: 'Entero positivo.' },
  { pos: 9, nombre: 'FECHA ESTIMADA', obligatorio: false, aliases: ['ETA', 'Fecha entrega', 'Fecha estimada entrega'], ejemplo: '30/06/2026' },
];

function ExcelFormatHelp() {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
        <p>
          La primera fila del Excel <strong>debe ser la cabecera</strong> con los nombres de las columnas. El orden NO es fijo: detectamos por el nombre. La detección ignora mayúsculas, tildes y caracteres especiales.
        </p>
        <p className="mt-2">
          Si una columna no obligatoria falta, la dejamos vacía. Solo <strong>PEDIDO</strong> y <strong>UNIDADES</strong> son imprescindibles.
        </p>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Nombre sugerido</th>
              <th className="px-3 py-2">Obligatorio</th>
              <th className="px-3 py-2">También acepta</th>
              <th className="px-3 py-2">Ejemplo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {EXCEL_COLUMNAS.map((col) => (
              <tr key={col.pos} className="align-top">
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{col.pos}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold text-slate-900">{col.nombre}</div>
                  {col.notas && <div className="mt-1 text-xs text-slate-500">{col.notas}</div>}
                </td>
                <td className="px-3 py-2">
                  {col.obligatorio
                    ? <Badge color="#dc2626">Sí</Badge>
                    : <span className="text-xs text-slate-400">no</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {col.aliases.length > 0 ? col.aliases.join(' · ') : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-700">{col.ejemplo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <p className="font-semibold">Cosas a tener en cuenta:</p>
        <ul className="ml-4 mt-1 list-disc space-y-0.5">
          <li>Una hoja Excel puede contener varios pedidos: <strong>las filas con el mismo valor en PEDIDO se agrupan en un único pedido_cliente</strong> con todas sus líneas.</li>
          <li>El pedido se crea siempre en estado <strong>Pdte. respuesta</strong>. Antes de avanzar el estado, todas las líneas deben tener SKU (o producto enlazado).</li>
          <li>Los SKU que no existen aún se dan de alta automáticamente como producto GAME (estado EN_FABRICACION, revisión pendiente, coste = COSTE UNIDADES del Excel).</li>
          <li>Las fechas aceptan formato dd/mm/yyyy, yyyy-mm-dd o serial Excel.</li>
        </ul>
      </div>
    </div>
  );
}

/** Hoy en hora local como 'YYYY-MM-DD' (valor de un <input type="date">). */
function hoyLocal(): string {
  return new Date().toLocaleDateString('sv-SE');
}

/** Líneas de un pedido, desplegadas bajo su fila en la pestaña «Por pedido». */
function LineasPedidoDesplegadas({ pedidoId, clienteCodigo, onAbrirLinea }: {
  pedidoId: string;
  clienteCodigo: string;
  onAbrirLinea: (lineaId: string) => void;
}) {
  const q = useQuery({
    queryKey: ['pedidos-cliente-lineas', clienteCodigo, 'pedido', pedidoId],
    queryFn: () => api.get<{ data: LineaRow[] }>(
      `/pedidos-cliente/lineas/full?cliente_codigo=${encodeURIComponent(clienteCodigo)}&pedido_cliente_id=${pedidoId}`,
    ),
  });
  if (q.isLoading) return <p className="py-1 text-xs text-slate-500">Cargando líneas…</p>;
  if (q.isError) return <p className="py-1 text-xs text-rose-700">No se pudieron cargar las líneas.</p>;
  const filas = q.data?.data ?? [];
  if (filas.length === 0) return <p className="py-1 text-xs text-slate-500">El pedido no tiene líneas.</p>;
  return (
    <table className="w-auto text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
          <th className="py-1 pr-3 font-medium">SKU</th>
          <th className="py-1 pr-3 font-medium">Producto</th>
          <th className="py-1 pr-3 text-right font-medium">Uds.</th>
          <th className="py-1 pr-3 text-right font-medium">Entregadas</th>
          <th className="py-1 pr-3 text-right font-medium">Pendientes</th>
          <th className="py-1 pr-3 text-right font-medium">Precio</th>
          <th className="py-1 pr-3 text-right font-medium">Subtotal</th>
          <th className="py-1 pr-3 font-medium">ETD</th>
          <th className="py-1 font-medium">ETA</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200/70">
        {filas.map((l) => {
          const pend = Number(l.unidades_pendientes ?? Math.max(0, l.unidades - (l.unidades_entregadas ?? 0)));
          const uds = Math.max(l.unidades, l.unidades_entregadas ?? 0);
          return (
            <tr key={l.linea_id} onClick={() => onAbrirLinea(l.linea_id)} className="cursor-pointer hover:bg-white">
              <td className="whitespace-nowrap py-1 pr-3 font-mono">{l.sku_cliente ?? l.producto_sku ?? '—'}</td>
              <td className="max-w-md truncate py-1 pr-3 text-slate-700" title={l.producto_nombre ?? l.descripcion ?? undefined}>{l.producto_nombre ?? l.descripcion ?? '—'}</td>
              <td className="py-1 pr-3 text-right font-mono">{fmt.int(l.unidades)}</td>
              <td className={`py-1 pr-3 text-right font-mono ${Number(l.unidades_entregadas) > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{fmt.int(Number(l.unidades_entregadas ?? 0))}</td>
              <td className={`py-1 pr-3 text-right font-mono ${pend === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt.int(pend)}</td>
              <td className="whitespace-nowrap py-1 pr-3 text-right">{fmt.eur(l.precio_unitario)}</td>
              <td className="whitespace-nowrap py-1 pr-3 text-right font-semibold">{fmt.eur((Number(l.precio_unitario) || 0) * uds)}</td>
              <td className="whitespace-nowrap py-1 pr-3">{fmt.date(l.etd)}</td>
              <td className="whitespace-nowrap py-1">{fmt.date(l.eta)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
